import { z } from 'zod'
import { emailSchema } from '../validators/email.validator.js'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'
import { coverImageSchema } from './cover-image.input.js'
import { vendorProfileFieldsSchema } from './vendor-profile.input.js'

/** Create a vendor (platform SUPER_ADMIN only). Its users are invited separately,
 * and the vendor fills in its own address afterward. */
export const createVendorInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(160),
  })
  .strict()

export type CreateVendorInput = z.infer<typeof createVendorInputSchema>

/** An optional address part: trimmed, capped, and normalized so an empty string
 * clears the field (stored as null) rather than persisting whitespace. */
const addressPart = z
  .string()
  .trim()
  .max(200)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional()

/** Update a vendor's business name, mailing address and profile (super admin, or
 * the vendor's own users). Every field is optional; omit one to leave it
 * unchanged. The profile's photos, links and packages have routes of their own. */
export const updateVendorInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(160).optional(),
    addressLine1: addressPart,
    addressLine2: addressPart,
    city: addressPart,
    state: addressPart,
    zip: z.string().trim().max(20).transform((v) => (v === '' ? null : v)).nullable().optional(),
    /** How far the vendor travels from its base, in miles (0–500); null clears it. */
    serviceRadiusMiles: z.number().int().min(0).max(500).nullable().optional(),
    /** Omit to leave unchanged, null to remove the cover, or a new image. */
    coverImageUrl: coverImageSchema.nullable().optional(),
    /** The profile's square logo: omit, null to remove, or a new image. */
    logoUrl: coverImageSchema.nullable().optional(),
    ...vendorProfileFieldsSchema,
  })
  .strict()

export type UpdateVendorInput = z.infer<typeof updateVendorInputSchema>

/**
 * Whether venues can find the company in search. An owner's choice; venues it
 * already works with find it either way.
 */
export const setVendorSearchListingInputSchema = z
  .object({
    hidden: z.boolean(),
  })
  .strict()

export type SetVendorSearchListingInput = z.infer<typeof setVendorSearchListingInputSchema>

/** Link a vendor to a venue (super admin only). The vendor comes from the route. */
export const linkVendorVenueInputSchema = z
  .object({
    venueId: z.string().uuid(),
  })
  .strict()

export type LinkVendorVenueInput = z.infer<typeof linkVendorVenueInputSchema>

/** Invite a user to manage a vendor. The vendor comes from the route; the user
 * is always created with the VENDOR role, outside any tenant. */
export const inviteVendorUserInputSchema = z
  .object({
    email: emailSchema,
    name: z.string().trim().min(1).max(200),
    /**
     * Invite them as an owner. Ignored for a vendor with no owner yet: its first
     * person always becomes one, since someone has to be able to invite the rest.
     */
    isOwner: z.boolean().optional(),
  })
  .strict()

export type InviteVendorUserInput = z.infer<typeof inviteVendorUserInputSchema>

/** Make one of a vendor's people an owner, or stop them being one. */
export const setVendorOwnerInputSchema = z
  .object({
    isOwner: z.boolean(),
  })
  .strict()

export type SetVendorOwnerInput = z.infer<typeof setVendorOwnerInputSchema>

/** Set the full set of services a vendor offers (self-configured by the vendor,
 * or a super admin). Replace-set semantics: the array is the new complete set;
 * un-offering a service hides its config but never deletes it. */
export const setVendorServicesInputSchema = z
  .object({
    services: z.array(vendorServiceSchema),
  })
  .strict()

export type SetVendorServicesInput = z.infer<typeof setVendorServicesInputSchema>

/** Set the full set of a vendor's services a venue approves (venue/tenant admin).
 * Replace-set: the array is the new complete set of approved services for this
 * vendor at this venue; only services the vendor actually offers are honored. */
export const setVendorVenueServicesInputSchema = z
  .object({
    services: z.array(vendorServiceSchema),
  })
  .strict()

export type SetVendorVenueServicesInput = z.infer<typeof setVendorVenueServicesInputSchema>

/** Search the listed vendors on the platform, by name and by what it offers. */
export const vendorSearchQuerySchema = z
  .object({
    q: z.string().trim().max(120).default(''),
    service: vendorServiceSchema.optional(),
  })
  .strict()

export type VendorSearchQuery = z.infer<typeof vendorSearchQuerySchema>

/**
 * A company that does not exist yet, set up by a venue manager with its owner,
 * who is invited to it. What it is here for becomes its first offering; after
 * that, what it offers is its own to elect (docs/planner-vendors.md, 15 and 26).
 */
export const newVendorCompanyInputSchema = z
  .object({
    companyName: z.string().trim().min(1, 'Company name is required').max(160),
    contactName: z.string().trim().min(1, 'Contact name is required').max(200),
    contactEmail: emailSchema,
  })
  .strict()

export type NewVendorCompanyInput = z.infer<typeof newVendorCompanyInputSchema>

/**
 * Who, for which service: an existing company picked from search, or a new
 * one set up in the same step (decision 16).
 */
export const vendorChoiceInputSchema = z.union([
  z.object({ vendorId: z.string().uuid(), service: vendorServiceSchema }).strict(),
  newVendorCompanyInputSchema.extend({ service: vendorServiceSchema }).strict(),
])

export type VendorChoiceInput = z.infer<typeof vendorChoiceInputSchema>

/** Settings > Vendors: set a company up at a venue, for one service. */
export const addVenueVendorInputSchema = vendorChoiceInputSchema

export type AddVenueVendorInput = z.infer<typeof addVenueVendorInputSchema>

/**
 * Put a company on an event for one service. Adding it for a service the venue
 * has not approved approves it (decision 5).
 */
export const assignEventVendorInputSchema = vendorChoiceInputSchema

export type AssignEventVendorInput = z.infer<typeof assignEventVendorInputSchema>

/** Correct the email of a company's owner who has not accepted, and invite them again. */
export const correctVendorOwnerEmailInputSchema = z
  .object({
    email: emailSchema,
  })
  .strict()

export type CorrectVendorOwnerEmailInput = z.infer<typeof correctVendorOwnerEmailInputSchema>
